import React, { useEffect, useState, useMemo } from 'react';
import { 
    Snackbar, Box, Card, Typography, Table, TableBody, TableCell, 
    TableContainer, TableHead, TableRow, Alert, CircularProgress, 
    ToggleButton, ToggleButtonGroup, Button, Avatar, Grid, IconButton
} from '@mui/material';
import { Download as DownloadIcon, RestaurantMenu as RestaurantMenuIcon, ArrowForward as ArrowForwardIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { reservationsAPI, getImageUrl } from '../../services/api';
import { toPersianNumber } from '../../utils/persianNumber';
import { formatPersianDate } from '../../utils/dateHelper';
import PersianDatePicker from '../../components/PersianDatePicker';
import * as XLSX from 'xlsx';
import { PieChart, Pie, Cell, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import { toJalaali, toGregorian } from 'jalaali-js';
import StaffReservationDialog from '../../components/StaffReservationDialog';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

interface DailyReservation {
    id: string;
    rowNumber: number;
    user: {
        id?: string;
        firstName: string;
        lastName: string;
        cardNumber: string;
        personnelCode: string;
    };
    meal: {
        title: string;
        imageUrl?: string;
    };
    mealType: 'breakfast' | 'lunch' | 'dinner';
    mealDate: string;
    createdAt: string;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#e84393', '#00cec9', '#fdcb6e'];

const monthNames = [
    'فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور',
    'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند'
];

const DailyReservations: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const hasPersonnelReservationPerm = user?.role === 'admin' || user?.role === 'superadmin' || user?.permissions?.includes('personnel-reservations');
    const [reservations, setReservations] = useState<DailyReservation[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedDate, setSelectedDate] = useState(() => {
        const today = new Date();
        return today.toISOString().split('T')[0];
    });
    const [selectedEndDate, setSelectedEndDate] = useState(() => {
        const today = new Date();
        return today.toISOString().split('T')[0];
    });
    const [mealTypeFilter, setMealTypeFilter] = useState<'all' | 'breakfast' | 'lunch' | 'dinner'>('all');
    const [isStaffDialogOpen, setIsStaffDialogOpen] = useState(false);


    // Monthly Stats
    const [currentJDate, setCurrentJDate] = useState(() => {
        const today = new Date();
        const j = toJalaali(today.getFullYear(), today.getMonth() + 1, today.getDate());
        return { jy: j.jy, jm: j.jm };
    });
    const [monthlyStats, setMonthlyStats] = useState<Record<number, number>>({});
    const [loadingMonth, setLoadingMonth] = useState(false);

    useEffect(() => {
        loadMonthlyStats();
    }, [currentJDate]);

    useEffect(() => {
        loadReservations();
    }, [selectedDate, selectedEndDate, mealTypeFilter]);

    const loadMonthlyStats = async () => {
        try {
            setLoadingMonth(true);
            const daysInMonth = currentJDate.jm <= 6 ? 31 : (currentJDate.jm <= 11 ? 30 : (((currentJDate.jy + 2346) % 128) < 30 ? 30 : 29));
            const startG = toGregorian(currentJDate.jy, currentJDate.jm, 1);
            const endG = toGregorian(currentJDate.jy, currentJDate.jm, daysInMonth);

            const startDateStr = `${startG.gy}-${String(startG.gm).padStart(2, '0')}-${String(startG.gd).padStart(2, '0')}`;
            const endDateStr = `${endG.gy}-${String(endG.gm).padStart(2, '0')}-${String(endG.gd).padStart(2, '0')}`;

            const stats = await reservationsAPI.getMonthlyStats(startDateStr, endDateStr);
            const statsMap: Record<number, number> = {};
            stats.forEach((s: any) => {
                const [y, m, d] = s.date.split('-');
                const j = toJalaali(parseInt(y, 10), parseInt(m, 10), parseInt(d, 10));
                if (j.jy === currentJDate.jy && j.jm === currentJDate.jm) {
                    statsMap[j.jd] = s.count;
                }
            });
            setMonthlyStats(statsMap);
        } catch (err) {
            console.error(err);
        } finally {
            setLoadingMonth(false);
        }
    };

    const loadReservations = async () => {
        try {
            setLoading(true);
            setError('');
            const mealType = mealTypeFilter === 'all' ? undefined : mealTypeFilter;
            const data = await reservationsAPI.getDailyReservations(selectedDate, selectedEndDate, mealType);
            setReservations(data);
        } catch (err: any) {
            setError('خطا در بارگذاری رزروها');
        } finally {
            setLoading(false);
        }
    };

    const navigateMonth = (direction: 'next' | 'prev') => {
        setCurrentJDate(prev => {
            let m = prev.jm;
            let y = prev.jy;
            if (direction === 'prev') {
                m--;
                if (m < 1) { m = 12; y--; }
            } else {
                m++;
                if (m > 12) { m = 1; y++; }
            }
            return { jy: y, jm: m };
        });
    };

    const handleSelectDay = (day: number) => {
        const gDate = toGregorian(currentJDate.jy, currentJDate.jm, day);
        const d = new Date(gDate.gy, gDate.gm - 1, gDate.gd);
        d.setHours(12, 0, 0, 0); // avoid timezone shifts
        const dateStr = d.toISOString().split('T')[0];
        
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const targetDate = new Date(dateStr);
        targetDate.setHours(0, 0, 0, 0);

        // Optional: Block selecting past dates even from the grid
        // if (targetDate < today) return;

        setSelectedDate(dateStr);
        setSelectedEndDate(dateStr);
    };

    const mealSummary = useMemo(() => {
        const summary: Record<string, { name: string, value: number, imageUrl?: string }> = {};
        reservations.forEach(r => {
            if (!summary[r.meal.title]) {
                summary[r.meal.title] = { name: r.meal.title, value: 0, imageUrl: r.meal.imageUrl };
            }
            summary[r.meal.title].value += 1;
        });
        return Object.values(summary).sort((a, b) => b.value - a.value);
    }, [reservations]);

    const formatTime = (dateStr: string) => {
        const date = new Date(dateStr);
        return date.toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' });
    };

    const handleExportExcel = () => {
        const data = reservations.map(r => ({
            'ردیف': r.rowNumber,
            'تاریخ سرو': formatPersianDate(r.mealDate),
            'نام و نام خانوادگی': `${r.user.firstName} ${r.user.lastName}`,
            'کد پرسنلی': r.user.personnelCode,
            'شماره کارت': r.user.cardNumber,
            'غذا': r.meal.title,
            'وعده': r.mealType === 'breakfast' ? 'صبحانه' : (r.mealType === 'lunch' ? 'ناهار' : 'شام'),
            'ساعت ثبت رزرو': formatTime(r.createdAt)
        }));

        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Reservations");
        
        XLSX.writeFile(wb, `reservations_${selectedDate}_to_${selectedEndDate}.xlsx`);
    };

    const renderCustomTooltip = ({ active, payload }: any) => {
        if (active && payload && payload.length) {
            return (
                <Box sx={{ bgcolor: 'white', p: 1.5, border: '1px solid #ccc', borderRadius: '8px', fontFamily: 'Vazir' }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{payload[0].name}</Typography>
                    <Typography variant="body2" color="primary">{`${payload[0].value} پرس`}</Typography>
                </Box>
            );
        }
        return null;
    };

    const daysInMonth = currentJDate.jm <= 6 ? 31 : (currentJDate.jm <= 11 ? 30 : (((currentJDate.jy + 2346) % 128) < 30 ? 30 : 29));
    const todayStr = new Date().toISOString().split('T')[0];

    return (
        <Box sx={{ fontFamily: 'Vazir, sans-serif' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: 'primary.main', mb: 1, fontFamily: 'Vazir, sans-serif' }}>
                        مدیریت رزروهای روزانه
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Vazir, sans-serif' }}>
                        آمار تجمیعی غذاهای رزرو شده و لیست نفرات جهت آماده‌سازی
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Box sx={{ width: 180, '& .MuiFormControl-root, & .MuiInputBase-root': { height: 48, borderRadius: '12px' }, '& .MuiOutlinedInput-input': { padding: '12.5px 14px' } }}>
                        <PersianDatePicker minDate={todayStr} value={selectedDate} onChange={setSelectedDate} label="از تاریخ" />
                    </Box>
                    <Box sx={{ width: 180, '& .MuiFormControl-root, & .MuiInputBase-root': { height: 48, borderRadius: '12px' }, '& .MuiOutlinedInput-input': { padding: '12.5px 14px' } }}>
                        <PersianDatePicker minDate={todayStr} value={selectedEndDate} onChange={setSelectedEndDate} label="تا تاریخ" />
                    </Box>
                    <ToggleButtonGroup value={mealTypeFilter} exclusive onChange={(_, value) => value && setMealTypeFilter(value)} sx={{ height: 48 }}>
                        <ToggleButton value="all" sx={{ fontFamily: 'Vazir, sans-serif', px: 3 }}>همه</ToggleButton>
                        <ToggleButton value="breakfast" sx={{ fontFamily: 'Vazir, sans-serif', px: 3 }}>صبحانه</ToggleButton>
                        <ToggleButton value="lunch" sx={{ fontFamily: 'Vazir, sans-serif', px: 3 }}>ناهار</ToggleButton>
                        <ToggleButton value="dinner" sx={{ fontFamily: 'Vazir, sans-serif', px: 3 }}>شام</ToggleButton>
                    </ToggleButtonGroup>
                    {hasPersonnelReservationPerm && (
                        <Button variant="contained" color="primary" onClick={() => setIsStaffDialogOpen(true)} sx={{ borderRadius: '12px', height: 48, fontFamily: 'Vazir, sans-serif', fontWeight: 700 }}>
                            ثبت رزرو پرسنل
                        </Button>
                    )}
                    <Button variant="contained" color="success" startIcon={<DownloadIcon />} onClick={handleExportExcel} disabled={reservations.length === 0} sx={{ borderRadius: '12px', height: 48, fontFamily: 'Vazir, sans-serif', fontWeight: 700 }}>
                        خروجی اکسل
                    </Button>
                </Box>
            </Box>

            {/* Quick Monthly Grid Section */}
            <Card sx={{ p: 2, mb: 4, borderRadius: '16px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        انتخاب سریع روز
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <IconButton onClick={() => navigateMonth('prev')} size="small">
                            <ArrowForwardIcon fontSize="small" />
                        </IconButton>
                        <Typography sx={{ fontWeight: 'bold', minWidth: 100, textAlign: 'center' }}>
                            {monthNames[currentJDate.jm - 1]} {toPersianNumber(currentJDate.jy)}
                        </Typography>
                        <IconButton onClick={() => navigateMonth('next')} size="small">
                            <ArrowBackIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </Box>

                {loadingMonth ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box sx={{ 
                        display: 'flex', 
                        flexWrap: 'nowrap', 
                        overflowX: 'auto',
                        gap: 1,
                        pb: 1,
                        // Custom scrollbar styling
                        '&::-webkit-scrollbar': { height: 6 },
                        '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: 4 }
                    }}>
                        {Array.from({ length: daysInMonth }).map((_, i) => {
                            const day = i + 1;
                            const count = monthlyStats[day] || 0;
                            const hasReservations = count > 0;
                            
                            const gDate = toGregorian(currentJDate.jy, currentJDate.jm, day);
                            const d = new Date(gDate.gy, gDate.gm - 1, gDate.gd);
                            const dayOfWeek = d.getDay();
                            const isThursday = dayOfWeek === 4;
                            const isFriday = dayOfWeek === 5;
                            
                            let headerBg = 'linear-gradient(45deg, #3b82f6, #60a5fa)';
                            if (isFriday) {
                                headerBg = 'linear-gradient(45deg, #ef4444, #f87171)';
                            } else if (isThursday) {
                                headerBg = 'linear-gradient(45deg, #f97316, #fb923c)';
                            }

                            return (
                                <Box
                                    key={day}
                                    onClick={() => handleSelectDay(day)}
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        minWidth: 42,
                                        height: 64,
                                        borderRadius: '8px',
                                        cursor: 'pointer',
                                        overflow: 'hidden',
                                        border: '1px solid rgba(0,0,0,0.05)',
                                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                        flexShrink: 0,
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                                        '&:hover': {
                                            transform: 'scale(1.05)',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                        }
                                    }}
                                >
                                    <Box sx={{
                                        height: '30%',
                                        background: headerBg,
                                        color: 'white',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontFamily: 'Vazir, sans-serif'
                                    }}>
                                        <Typography variant="caption" sx={{ fontWeight: 800, fontSize: '0.75rem', lineHeight: 1 }}>
                                            {toPersianNumber(day)}
                                        </Typography>
                                    </Box>
                                    <Box sx={{
                                        height: '70%',
                                        bgcolor: hasReservations ? '#10b981' : '#f8fafc',
                                        color: hasReservations ? 'white' : 'text.secondary',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontFamily: 'Vazir, sans-serif'
                                    }}>
                                        <Typography variant="caption" sx={{ fontWeight: 800, fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            {hasReservations ? (
                                                <>
                                                    <RestaurantMenuIcon sx={{ fontSize: '0.9rem' }} />
                                                    {toPersianNumber(count)}
                                                </>
                                            ) : '-'}
                                        </Typography>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                )}
            </Card>

            <Snackbar open={!!error} autoHideDuration={2000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert severity="error" sx={{ width: '100%', fontFamily: 'Vazir' }} onClose={() => setError('')}>{error}</Alert>
            </Snackbar>

            {!loading && mealSummary.length > 0 && (
                <Box sx={{ mb: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                            <RestaurantMenuIcon color="primary" /> آمار کلی جهت پخت غذا
                        </Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white', px: 2, py: 0.5, borderRadius: '8px' }}>
                            مجموع کل: {toPersianNumber(reservations.length)} پرس
                        </Typography>
                    </Box>
                    
                    <Grid container spacing={3}>
                        {/* Food Cards */}
                        <Grid size={{ xs: 12, md: 8 }}>
                            <Grid container spacing={2}>
                                {mealSummary.map((meal, idx) => (
                                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={meal.name}>
                                        <Card sx={{ 
                                            display: 'flex', alignItems: 'center', p: 2, borderRadius: '16px', 
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.05)', height: '100%',
                                            borderLeft: `4px solid ${COLORS[idx % COLORS.length]}`
                                        }}>
                                            <Avatar 
                                                src={meal.imageUrl ? getImageUrl(meal.imageUrl) : undefined} 
                                                variant="rounded" 
                                                sx={{ width: 60, height: 60, mr: 2, bgcolor: 'grey.100' }}
                                            >
                                                <RestaurantMenuIcon color="action" />
                                            </Avatar>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5, lineHeight: 1.2 }}>
                                                    {meal.name}
                                                </Typography>
                                                <Typography variant="h5" color="primary.main" sx={{ fontWeight: 800 }}>
                                                    {meal.value} <Typography component="span" variant="caption" color="text.secondary">پرس</Typography>
                                                </Typography>
                                            </Box>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        </Grid>

                        {/* Chart */}
                        <Grid size={{ xs: 12, md: 4 }}>
                            <Card sx={{ borderRadius: '16px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', height: '100%', p: 2 }}>
                                <Typography variant="subtitle2" sx={{ textAlign: 'center', fontWeight: 'bold', mb: 1, color: 'text.secondary' }}>
                                    ترکیب سفارشات
                                </Typography>
                                <Box sx={{ width: '100%', height: 250 }}>
                                    <ResponsiveContainer>
                                        <PieChart>
                                            <Pie
                                                data={mealSummary}
                                                innerRadius={60}
                                                outerRadius={90}
                                                paddingAngle={5}
                                                dataKey="value"
                                                stroke="none"
                                            >
                                                {mealSummary.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <RechartsTooltip content={renderCustomTooltip} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </Box>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1.5, mt: 2 }}>
                                    {mealSummary.map((m, idx) => (
                                        <Box key={m.name} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: COLORS[idx % COLORS.length] }} />
                                            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                                {m.name}: {toPersianNumber(m.value)}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Card>
                        </Grid>
                    </Grid>
                </Box>
            )}

            <Card sx={{ borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                <TableContainer>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 10 }}>
                            <CircularProgress size={50} />
                        </Box>
                    ) : (
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: 'rgba(25, 118, 210, 0.08)' }}>
                                    <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 'bold', textAlign: 'center' }}>ردیف</TableCell>
                                    <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 'bold' }}>تاریخ</TableCell>
                                    <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 'bold' }}>نام و نام خانوادگی</TableCell>
                                    <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 'bold' }}>کد پرسنلی</TableCell>
                                    <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 'bold' }}>شماره کارت</TableCell>
                                    <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 'bold' }}>غذا</TableCell>
                                    <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 'bold', textAlign: 'center' }}>وعده</TableCell>
                                    <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 'bold', textAlign: 'center' }}>ساعت رزرو</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {reservations.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                                            <Typography variant="body1" color="text.secondary" sx={{ fontFamily: 'Vazir' }}>
                                                هیچ رزروی برای این بازه زمانی یافت نشد
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    reservations.map((reservation) => (
                                        <TableRow key={reservation.id} hover>
                                            <TableCell sx={{ fontFamily: 'Vazir', textAlign: 'center' }}>{toPersianNumber(reservation.rowNumber)}</TableCell>
                                            <TableCell sx={{ fontFamily: 'Vazir' }}>{formatPersianDate(reservation.mealDate)}</TableCell>
                                            <TableCell 
                                                sx={{ 
                                                    fontFamily: 'Vazir', 
                                                    fontWeight: 'bold',
                                                    color: reservation.user.id ? 'primary.main' : 'inherit',
                                                    cursor: reservation.user.id ? 'pointer' : 'default',
                                                    '&:hover': reservation.user.id ? { textDecoration: 'underline' } : {}
                                                }}
                                                onClick={() => {
                                                    if (reservation.user.id) {
                                                        navigate(`/admin/users/${reservation.user.id}/reports`);
                                                    }
                                                }}
                                                title={reservation.user.id ? "مشاهده گزارش و تمامی رزروهای این کاربر" : undefined}
                                            >
                                                {reservation.user.firstName} {reservation.user.lastName}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily: 'Vazir' }}>{toPersianNumber(reservation.user.personnelCode)}</TableCell>
                                            <TableCell sx={{ fontFamily: 'Vazir' }}>{toPersianNumber(reservation.user.cardNumber)}</TableCell>
                                            <TableCell sx={{ fontFamily: 'Vazir' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    {reservation.meal.imageUrl && (
                                                        <Avatar src={getImageUrl(reservation.meal.imageUrl)} variant="rounded" sx={{ width: 30, height: 30 }} />
                                                    )}
                                                    {reservation.meal.title}
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ fontFamily: 'Vazir', textAlign: 'center' }}>
                                                <Typography variant="body2" sx={{ 
                                                    bgcolor: reservation.mealType === 'breakfast' ? 'warning.light' : (reservation.mealType === 'lunch' ? 'success.light' : 'info.light'),
                                                    color: 'white', py: 0.5, px: 1, borderRadius: '8px', fontSize: '0.8rem'
                                                }}>
                                                    {reservation.mealType === 'breakfast' ? 'صبحانه' : (reservation.mealType === 'lunch' ? 'ناهار' : 'شام')}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ fontFamily: 'Vazir', textAlign: 'center' }}>{toPersianNumber(formatTime(reservation.createdAt))}</TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    )}
                </TableContainer>
            </Card>

            <StaffReservationDialog 
                open={isStaffDialogOpen} 
                onClose={() => setIsStaffDialogOpen(false)} 
                onSuccess={() => {
                    loadReservations();
                    // Optionally show a success snackbar here
                }} 
            />
        </Box>
    );
};

export default DailyReservations;
